/**
 * The text of an entry's HTML, a block at a time, for search (docs/SEARCH-PLAN.md section
 * 3.2). A worker has no DOM, so this reads the HTML itself, following the desktop's
 * `search.RichText` (which walks a jsoup document): a block element or a `<br>` ends a
 * block; text joins across inline tags (a phrase with bold or a link in the middle reads
 * as one); `<script>` and `<style>` contents, comments, attributes and anything in
 * `<head>` are never text; empty blocks are dropped. It only reads: it never builds HTML.
 *
 * It's a reader for what editors write, not a full HTML parser: it handles the implicit
 * closing those editors rely on (a `<p>` or `<li>` ends the one before it) and decodes
 * the common named entities and every numeric one. schema/fixtures/search-cases.txt keeps
 * it in step with the desktop.
 */

/** `RichText.BLOCKS`: the elements that make a block of text. The page's highlighting splits text the same way. */
export const TEXT_BLOCKS: ReadonlySet<string> = new Set(['p', 'div', 'li', 'ul', 'ol', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'table', 'tr', 'td', 'th', 'body', 'section', 'article']);
const VOID = new Set(['area', 'base', 'br', 'col', 'embed', 'hr', 'img', 'input', 'link', 'meta', 'param', 'source', 'track', 'wbr']);
/** Whose contents are raw text, read up to their own end tag. */
const RAW = new Set(['script', 'style', 'textarea', 'title', 'xmp']);
const SKIPPED = new Set(['script', 'style']);
/** Start tags that end an open `<p>`, as the HTML parser does. */
const CLOSES_P = new Set([
  'p', 'div', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'table', 'section', 'article',
  'hr', 'dl', 'dd', 'dt', 'form', 'fieldset', 'figure', 'header', 'footer', 'main', 'nav', 'aside', 'address', 'details',
]);
const HEADINGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);

const NAMED: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', ensp: ' ', emsp: ' ', thinsp: ' ',
  ndash: '–', mdash: '—', hellip: '…', lsquo: '‘', rsquo: '’', sbquo: '‚', ldquo: '“', rdquo: '”', bdquo: '„',
  laquo: '«', raquo: '»', bull: '•', middot: '·', copy: '©', reg: '®', trade: '™', deg: '°', plusmn: '±', times: '×',
  divide: '÷', frac12: '½', frac14: '¼', frac34: '¾', sect: '§', para: '¶', dagger: '†', Dagger: '‡', prime: '′',
  Prime: '″', euro: '€', pound: '£', yen: '¥', cent: '¢', iexcl: '¡', iquest: '¿', shy: '­', zwj: '‍',
  zwnj: '‌', larr: '←', rarr: '→', uarr: '↑', darr: '↓', harr: '↔',
};

const ENTITY = /&(#[xX][0-9a-fA-F]+|#[0-9]+|[A-Za-z][A-Za-z0-9]*);/g;

function decodeOne(whole: string, body: string): string {
  if (body[0] === '#') {
    const code = body[1] === 'x' || body[1] === 'X' ? parseInt(body.slice(2), 16) : parseInt(body.slice(1), 10);
    if (code === 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff)) return '�';
    return String.fromCodePoint(code);
  }
  return NAMED[body] ?? whole;
}

/** Entities decoded as the HTML parser does: named ones from the list above (`;` required), and every numeric one. */
export function decodeEntities(text: string): string {
  return text.replace(ENTITY, (whole, body: string) => decodeOne(whole, body));
}

/**
 * `raw` decoded, with where each of the text's characters starts in `raw`: `at[i]` for
 * character `i`, and `at[text.length] = raw.length`. An entity's characters all start
 * where the entity does, so an edit never cuts one in half.
 */
function decodeWithPositions(raw: string): { text: string; at: number[] } {
  let text = '';
  const at: number[] = [];
  let last = 0;
  ENTITY.lastIndex = 0;
  for (let m = ENTITY.exec(raw); m !== null; m = ENTITY.exec(raw)) {
    for (let k = last; k < m.index; k++) {
      text += raw[k];
      at.push(k);
    }
    const decoded = decodeOne(m[0], m[1]!);
    for (let k = 0; k < decoded.length; k++) at.push(m.index);
    text += decoded;
    last = m.index + m[0].length;
  }
  for (let k = last; k < raw.length; k++) {
    text += raw[k];
    at.push(k);
  }
  at.push(raw.length);
  return { text, at };
}

/** One text node's part of a block: its text, where it starts in the block, and where its characters sit in the HTML. */
export interface TextPiece {
  readonly start: number;
  readonly text: string;
  /** `at[i]`: where character `i` starts in the HTML; `at[text.length]`: where the node's text ends. */
  readonly at: readonly number[];
  /** Whether the text node is inside a link (`<a>`), which Scan for Connections leaves alone. */
  readonly inLink: boolean;
}

export interface TextBlock {
  readonly text: string;
  readonly pieces: readonly TextPiece[];
}

interface Open {
  readonly tag: string;
  /** Each element's own number, so a block's owner can be told apart from another of the same tag. */
  readonly id: number;
}

/** The HTML's text, block by block, as `RichText.blockTexts` gives it. */
export function textBlocks(html: string): string[] {
  return readTextBlocks(html).map((block) => block.text);
}

/** The HTML's text blocks, each with its text nodes and where their characters sit in the HTML (for replacing, htmlReplace.ts). */
export function readTextBlocks(html: string): TextBlock[] {
  const blocks: TextBlock[] = [];
  const stack: Open[] = [];
  let nextId = 1;
  let text: string | null = null;
  let pieces: TextPiece[] = [];
  let owner = -1;

  const close = () => {
    if (text !== null && text !== '') blocks.push({ text, pieces });
    text = null;
    pieces = [];
    owner = -1;
  };
  const blockOwner = () => {
    for (let i = stack.length - 1; i >= 0; i--) if (TEXT_BLOCKS.has(stack[i]!.tag)) return stack[i]!.id;
    return 0; // the body
  };
  const inside = (tags: ReadonlySet<string>) => stack.some((open) => tags.has(open.tag));
  /** The text node between `from` and `to` in the HTML. */
  const addText = (from: number, to: number) => {
    if (to <= from || inside(SKIPPED) || stack.some((open) => open.tag === 'head')) return;
    const { text: decoded, at } = decodeWithPositions(html.slice(from, to));
    if (decoded === '') return;
    const block = blockOwner();
    if (text !== null && owner !== block) close();
    if (text === null) {
      text = '';
      owner = block;
    }
    pieces.push({ start: text.length, text: decoded, at: at.map((k) => k + from), inLink: stack.some((open) => open.tag === 'a') });
    text += decoded;
  };
  const pop = (index: number) => {
    while (stack.length > index) {
      const open = stack.pop()!;
      if (TEXT_BLOCKS.has(open.tag)) close();
    }
  };
  const popTo = (tags: ReadonlySet<string>, stopAt: ReadonlySet<string> = new Set()) => {
    for (let i = stack.length - 1; i >= 0; i--) {
      const tag = stack[i]!.tag;
      if (tags.has(tag)) {
        pop(i);
        return;
      }
      if (stopAt.has(tag)) return;
    }
  };

  let i = 0;
  const n = html.length;
  while (i < n) {
    const lt = html.indexOf('<', i);
    if (lt < 0) {
      addText(i, n);
      break;
    }
    if (lt > i) addText(i, lt);
    // Comments, doctypes and other markup declarations are never text.
    if (html.startsWith('<!--', lt)) {
      const end = html.indexOf('-->', lt + 4);
      i = end < 0 ? n : end + 3;
      continue;
    }
    if (html[lt + 1] === '!' || html[lt + 1] === '?') {
      const end = html.indexOf('>', lt);
      i = end < 0 ? n : end + 1;
      continue;
    }
    const tagMatch = /^<(\/?)([A-Za-z][A-Za-z0-9-]*)/.exec(html.slice(lt, lt + 64));
    if (!tagMatch) {
      // A `<` that starts no tag is text.
      addText(lt, lt + 1);
      i = lt + 1;
      continue;
    }
    const end = endOfTag(html, lt + tagMatch[0].length);
    const closing = tagMatch[1] === '/';
    const tag = tagMatch[2]!.toLowerCase();
    const selfClosed = html[end - 2] === '/';
    i = end;

    if (closing) {
      if (tag === 'br') {
        close(); // `</br>` is read as `<br>`
        continue;
      }
      popTo(new Set([tag]));
      continue;
    }

    // Start tags.
    if (tag === 'br' || TEXT_BLOCKS.has(tag)) close();
    if (CLOSES_P.has(tag)) popTo(new Set(['p']), new Set(['button', 'table', 'td', 'th']));
    if (tag === 'li') popTo(new Set(['li']), new Set(['ul', 'ol']));
    if (tag === 'dd' || tag === 'dt') popTo(new Set(['dd', 'dt']), new Set(['dl']));
    if (tag === 'td' || tag === 'th') popTo(new Set(['td', 'th']), new Set(['tr', 'table']));
    if (tag === 'tr') popTo(new Set(['tr']), new Set(['table']));
    if (HEADINGS.has(tag) && stack.length > 0 && HEADINGS.has(stack[stack.length - 1]!.tag)) pop(stack.length - 1);
    if (tag === 'body') popTo(new Set(['head']));
    if (VOID.has(tag) || (selfClosed && !TEXT_BLOCKS.has(tag) && !RAW.has(tag))) continue;

    stack.push({ tag, id: nextId++ });
    if (RAW.has(tag)) {
      const closeAt = html.toLowerCase().indexOf(`</${tag}`, i);
      if (!SKIPPED.has(tag)) addText(i, closeAt < 0 ? n : closeAt);
      i = closeAt < 0 ? n : endOfTag(html, closeAt + tag.length + 2);
      pop(stack.length - 1);
    }
  }
  close();
  return blocks;
}

/** Where a tag that started before `from` ends: after its `>`, skipping `>` inside quoted attribute values. */
function endOfTag(html: string, from: number): number {
  let quote: string | null = null;
  for (let i = from; i < html.length; i++) {
    const c = html[i]!;
    if (quote) {
      if (c === quote) quote = null;
    } else if (c === '"' || c === "'") {
      quote = c;
    } else if (c === '>') {
      return i + 1;
    }
  }
  return html.length;
}
